import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import {camelCase, startCase, upperFirst} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import Draggable, {DraggableData, DraggableEvent} from 'react-draggable';

import ModalFixed from '../ModalFixed';
import {DataGridColumn, DataGridRow} from '.';

interface Props<T extends DataGridRow> {
  open: boolean;
  columns: DataGridColumn<T>[];
  rows: T[];
  shownFields: string[];
  defaultShownFields?: string[];
  columnOrder: string[];
  columnWidths: Record<string, number>;
  onClose: () => void;
  onApply: (newOrder: string[], newShownFields: string[]) => void;
}

interface DraggableColumnItem {
  field: string;
  position: number;
  index: number;
  ref: React.RefObject<HTMLDivElement>;
  positionShift?: number;
  newPosition?: number;
}

const titleCase = (value: string) =>
  upperFirst(startCase(camelCase(value)).toLowerCase());

const getColumnLabel = <T extends DataGridRow>(
  column: DataGridColumn<T>
): string => {
  return column.headerName ?? titleCase(column.field);
};

export const DataGridArrangeColumnsModal = <T extends DataGridRow>({
  open,
  columns,
  rows,
  shownFields,
  defaultShownFields,
  columnOrder,
  columnWidths: _columnWidths,
  onClose,
  onApply,
}: Props<T>) => {
  const effectiveColumnOrder = useMemo(() => {
    const allFields = columns.map((c) => c.field);

    if (!columnOrder || columnOrder.length === 0) {
      return allFields;
    }

    const savedSet = new Set(columnOrder);
    const newFieldsWithIndexes = allFields
      .map((field, index) => ({field, index}))
      .filter(({field}) => !savedSet.has(field));

    if (newFieldsWithIndexes.length === 0) {
      return columnOrder;
    }

    const result = [...columnOrder];

    newFieldsWithIndexes.forEach(({field, index}) => {
      result.splice(index, 0, field);
    });

    return result;
  }, [columnOrder, columns]);

  const orderableColumns = useMemo(
    () => effectiveColumnOrder.filter((field) => {
      const col = columns.find((c) => c.field === field);
      return col && col.type !== 'select' && col.type !== 'actions';
    }),
    [effectiveColumnOrder, columns]
  );

  const [tempOrder, setTempOrder] = useState<string[]>(effectiveColumnOrder);
  const [tempShownFields, setTempShownFields] = useState<string[]>(shownFields);

  const initialRefs = useMemo(
    () => orderableColumns.map(() => ({current: null as HTMLDivElement | null})),
    []
  );

  const [draggableLayout, setDraggableLayout] = useState<DraggableColumnItem[]>(
    () =>
      orderableColumns.map((field, index) => ({
        field,
        position: index,
        index,
        ref: initialRefs[index],
      }))
  );

  useEffect(() => {
    if (open) {
      setTempOrder(effectiveColumnOrder);
      setTempShownFields(shownFields);

      const newOrderableColumns = effectiveColumnOrder.filter((field) => {
        const col = columns.find((c) => c.field === field);
        return col && col.type !== 'select' && col.type !== 'actions';
      });

      const newRefs = newOrderableColumns.map(() => ({
        current: null as HTMLDivElement | null,
      }));

      setDraggableLayout(
        newOrderableColumns.map((field, index) => ({
          field,
          position: index,
          index,
          ref: newRefs[index],
        }))
      );
    }
  }, [open, effectiveColumnOrder, shownFields, columns]);

  const sampleRows = useMemo(() => rows.slice(0, 5), [rows]);

  const columnsMap = useMemo(
    () => new Map(columns.map((col) => [col.field, col])),
    [columns]
  );

  const previewColumns = useMemo(() => {
    const ordered: DataGridColumn<T>[] = [];
    const processedFields = new Set<string>();

    tempOrder.forEach((field) => {
      const col = columnsMap.get(field);
      if (col && col.type !== 'select' && col.type !== 'actions' && tempShownFields.includes(field)) {
        ordered.push(col);
        processedFields.add(field);
      }
    });

    columns.forEach((col) => {
      if (
        col.type !== 'select' &&
        col.type !== 'actions' &&
        !processedFields.has(col.field) &&
        tempShownFields.includes(col.field)
      ) {
        ordered.push(col);
      }
    });

    return ordered;
  }, [columns, columnsMap, tempOrder, tempShownFields]);

  const handleToggleVisibility = (field: string) => {
    setTempShownFields((prev) => {
      if (prev.includes(field)) {
        return prev.filter((f) => f !== field);
      }
      return [...prev, field];
    });
  };

  const getItemHeight = (): number => {
    return draggableLayout[0]?.ref.current?.clientHeight ?? 48;
  };

  const between = (num1: number, num2: number, target: number): boolean => {
    if (target >= num1 && target <= num2) {
      return true;
    }
    if (target >= num2 && target <= num1) {
      return true;
    }
    return false;
  };

  const getDraggedPosition = (
    layout: DraggableColumnItem,
    data: DraggableData
  ): number => {
    const height = getItemHeight();
    const max = draggableLayout.length - 1;
    const currentPosition = layout.position;
    const {y} = data;

    const calculatedPosition =
      currentPosition +
      Math.sign(y / height) * Math.round(Math.abs(y / height));

    return calculatedPosition < 0
      ? 0
      : calculatedPosition > max
        ? max
        : calculatedPosition;
  };

  const updatePosition = (
    layout: DraggableColumnItem,
    _data: DraggableData,
    position: number
  ) => {
    const height = getItemHeight();
    const initialPosition = layout.position;
    const shift = initialPosition < position ? -1 : 1;

    draggableLayout.forEach((item) => {
      if (between(initialPosition, position, item.position)) {
        if (item.position !== initialPosition) {
          item.positionShift = shift;
          item.newPosition = item.position + shift;
        } else {
          item.positionShift = 0;
          item.newPosition = position;
        }
      } else {
        item.positionShift = 0;
        item.newPosition = item.position;
      }
    });

    draggableLayout.forEach((item) => {
      const y = height * (item.positionShift ?? 0);
      if (item.ref && item.ref.current) {
        if (item.position !== initialPosition) {
          item.ref.current.style.transform = `translate(0px, ${y}px)`;
        }
      }
    });
  };

  const handleDrag = useCallback(
    (index: number) => (_e: DraggableEvent, data: DraggableData) => {
      const layout = draggableLayout[index];
      if (!layout) {
        return;
      }
      const newPosition = getDraggedPosition(layout, data);
      updatePosition(layout, data, newPosition);
    },
    [draggableLayout]
  );

  const handleDragStop = useCallback(
    (_index: number) => () => {
      const newLayout = [...draggableLayout]
        .sort(
          (a, b) =>
            (a.newPosition ?? a.position) - (b.newPosition ?? b.position)
        )
        .map((item, idx) => ({
          ...item,
          position: idx,
          index: idx,
          positionShift: 0,
          newPosition: idx,
        }));

      setDraggableLayout(newLayout);

      draggableLayout.forEach((item) => {
        if (item.ref && item.ref.current) {
          item.ref.current.style.transform = 'translate(0px, 0px)';
        }
      });

      const newOrderableOrder = newLayout.map((item) => item.field);

      let orderableIndex = 0;
      const finalOrder = effectiveColumnOrder.map((field) => {
        const col = columns.find((c) => c.field === field);
        if (col && (col.type === 'select' || col.type === 'actions')) {
          return field;
        }
        return newOrderableOrder[orderableIndex++];
      });

      setTempOrder(finalOrder);
    },
    [draggableLayout, effectiveColumnOrder, columns]
  );

  const handleApply = () => {
    onApply(tempOrder, tempShownFields);
  };

  const handleRestoreDefaults = () => {
    const defaultOrder = columns.map((c) => c.field);
    setTempOrder(defaultOrder);

    if (defaultShownFields) {
      setTempShownFields(defaultShownFields);
    }

    const defaultOrderableColumns = defaultOrder.filter((field) => {
      const col = columns.find((c) => c.field === field);
      return col && col.type !== 'select' && col.type !== 'actions';
    });

    const newRefs = defaultOrderableColumns.map(() => ({
      current: null as HTMLDivElement | null,
    }));
    setDraggableLayout(
      defaultOrderableColumns.map((field, index) => ({
        field,
        position: index,
        index,
        ref: newRefs[index],
      }))
    );
  };

  const handleCancel = () => {
    setTempOrder(effectiveColumnOrder);
    setTempShownFields(shownFields);
    const newRefs = orderableColumns.map(() => ({
      current: null as HTMLDivElement | null,
    }));
    setDraggableLayout(
      orderableColumns.map((field, index) => ({
        field,
        position: index,
        index,
        ref: newRefs[index],
      }))
    );
    onClose();
  };

  return (
    <ModalFixed open={open} onClose={handleCancel} width={1200}>
      <>
        <DialogTitle>
          <Typography variant="h6" component="div">
            Customize Columns
          </Typography>
        </DialogTitle>
        <DialogContent sx={{p: 0, overflow: 'hidden'}}>
        <Box display="flex" gap={0} height={500} sx={{overflow: 'hidden'}}>
          <Box
            flex="0 0 280px"
            sx={{
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{p: 2, pb: 1}}
            >
              Column Order:
            </Typography>
            <Box sx={{px: 2, pb: 1}}>
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={handleRestoreDefaults}
              >
                Restore Defaults
              </Button>
            </Box>
            <Box
              sx={{
                p: 2,
                pt: 1,
                overflow: 'auto',
                flex: 1,
              }}
            >
              {draggableLayout.map((item, index) => {
                const column = columnsMap.get(item.field);
                if (!column) {
                  return null;
                }
                const label = getColumnLabel(column);
                const isVisible = tempShownFields.includes(item.field);

                return (
                  <Draggable
                    key={item.field}
                    nodeRef={item.ref}
                    axis="y"
                    handle=".drag-handle"
                    onDrag={handleDrag(index)}
                    onStop={handleDragStop(index)}
                    position={{x: 0, y: 0}}
                  >
                    <Box
                      ref={item.ref}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        mb: 0.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        cursor: 'move',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <DragIndicatorIcon
                        className="drag-handle"
                        sx={{
                          mr: 1,
                          color: 'action.active',
                          cursor: 'grab',
                          '&:active': {
                            cursor: 'grabbing',
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isVisible}
                            onChange={() => handleToggleVisibility(item.field)}
                            size="small"
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: !isVisible
                                ? 'line-through'
                                : 'none',
                              color: !isVisible
                                ? 'text.disabled'
                                : 'text.primary',
                            }}
                          >
                            {label}
                          </Typography>
                        }
                        sx={{flex: 1, m: 0}}
                      />
                    </Box>
                  </Draggable>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              minHeight: 0,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{p: 2, pb: 1, flexShrink: 0}}
            >
              Preview:
            </Typography>
            <Box
              sx={{
                overflow: 'auto',
                flex: 1,
                p: 2,
                minWidth: 0,
              }}
            >
              <Box
                component="table"
                sx={{
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem',
                }}
              >
                <Box component="thead">
                  <Box component="tr">
                    {previewColumns.map((col) => {
                      const headerContent = col.renderHeader
                        ? col.renderHeader({
                            field: col.field,
                            headerName: col.headerName,
                          })
                        : getColumnLabel(col);

                      return (
                        <Box
                          component="th"
                          key={col.field}
                          sx={{
                            p: 1,
                            borderBottom: '2px solid',
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                            textAlign: 'left',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {headerContent}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                <Box component="tbody">
                  {sampleRows.map((row, rowIdx) => (
                    <Box component="tr" key={rowIdx}>
                      {previewColumns.map((col) => {
                        const value = col.valueGetter
                          ? col.valueGetter({row, field: col.field})
                          : row[col.field];

                        let cellContent;
                        if (col.renderCell) {
                          cellContent = col.renderCell({
                            row,
                            field: col.field,
                            value,
                            valueFormatter: col.valueFormatter,
                          });
                        } else if (col.valueFormatter) {
                          cellContent = col.valueFormatter({
                            row,
                            field: col.field,
                            value,
                          });
                        } else {
                          cellContent =
                            value !== null && value !== undefined
                              ? String(value)
                              : '';
                        }

                        return (
                          <Box
                            component="td"
                            key={col.field}
                            sx={{
                              p: 1,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {cellContent}
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          Apply
        </Button>
      </DialogActions>
      </>
    </ModalFixed>
  );
};
